import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  FileCheck, 
  Search, 
  Truck,
  Calendar, 
  Gauge,
  Download,
  ExternalLink,
  Wrench,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  Database
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { maintenanceService } from '../services/maintenanceService';
import requisitionValesService from '../services/requisitionValesService';
import MaintenanceQueryModal from '../components/MaintenanceQueryModal';
import api from '../services/api';

const MaintenancesPage = () => {
  const navigate = useNavigate();
  const [maintenances, setMaintenances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Estados para paginación y ordenamiento
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortField, setSortField] = useState('FechaDeMantenimiento');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Estados para filtros
  const [filters, setFilters] = useState({
    tipo: '',
    fechaDesde: '',
    fechaHasta: '',
    kilometrajeMin: '',
    kilometrajeMax: '',
    serie: '',
    numeroEconomico: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showQueryModal, setShowQueryModal] = useState(false);
  const [trucks, setTrucks] = useState([]);

  // Cargar trucks para el modal de consulta
  useEffect(() => {
    const fetchTrucks = async () => {
      try {
        // Pedimos un límite alto para tener todas las unidades disponibles en el modal
        const response = await api.get('/trucks?limit=1000');
        const data = response.data;
        if (data.success) {
          setTrucks(data.data || []);
          console.log('Trucks cargados para modal (total):', (data.data || []).length);
        } else {
          console.warn('Respuesta inesperada trucks:', data);
        }
      } catch (error) {
        console.error('Error cargando trucks:', error);
      }
    };
    fetchTrucks();
  }, []);

  // Cargar mantenimientos
  useEffect(() => {
    const fetchMaintenances = async () => {
      try {
        setLoading(true);
        const data = await maintenanceService.getAll();
        console.log('Datos recibidos del backend:', data[0]); // Agregamos log para depurar
        
        // Arreglar el caso de la propiedad número económico
        const fixedData = data.map(item => {
          // Verificamos todas las posibles formas en que podría venir el número económico
          if (!item.NumeroEconomico && item.numero_economico) {
            item.NumeroEconomico = item.numero_economico;
          }
          
          // También revisamos si está en la entidad del vehículo
          if (!item.NumeroEconomico && item.Truck && item.Truck.numero_economico) {
            item.NumeroEconomico = item.Truck.numero_economico;
          }
          
          // Extraer de VehiculoDescripcion como último recurso
          if (!item.NumeroEconomico && item.VehiculoDescripcion) {
            const parts = item.VehiculoDescripcion.split('-');
            if (parts.length > 1) {
              item.NumeroEconomico = parts[parts.length - 1].trim();
            }
          }
          
          return item;
        });
        
        setMaintenances(fixedData);
        setLoading(false);
      } catch (err) {
        console.error('Error al cargar mantenimientos:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchMaintenances();
  }, []);
  
  // Manejar cambio en los filtros
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setCurrentPage(1); // Resetear paginación al filtrar
  };
  
  // Limpiar todos los filtros
  const clearFilters = () => {
    setFilters({
      tipo: '',
      fechaDesde: '',
      fechaHasta: '',
      kilometrajeMin: '',
      kilometrajeMax: '',
      serie: '',
      numeroEconomico: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Manejar ordenamiento de columnas
  const handleSort = (field) => {
    if (sortField === field) {
      // Si ya estamos ordenando por este campo, cambiar la dirección
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Si es un nuevo campo, ordenar ascendente por defecto
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  // Aplicar filtros, ordenamiento y búsqueda
  const filteredAndSortedMaintenances = useMemo(() => {
    // Paso 1: Aplicar búsqueda general
    let result = maintenances.filter(maintenance => {
      const searchTermLower = searchTerm.toLowerCase();
      return !searchTerm || (
        (maintenance.Serie && maintenance.Serie.toLowerCase().includes(searchTermLower)) ||
        (maintenance.TipoDeMantenimiento && maintenance.TipoDeMantenimiento.toLowerCase().includes(searchTermLower)) ||
        (maintenance.NumeroEconomico && maintenance.NumeroEconomico.toLowerCase().includes(searchTermLower)) ||
        (maintenance.VehiculoDescripcion && maintenance.VehiculoDescripcion.toLowerCase().includes(searchTermLower))
      );
    });
    
    // Paso 2: Aplicar filtros específicos
    result = result.filter(m => {
      // Filtro por tipo de mantenimiento
      if (filters.tipo && m.TipoDeMantenimiento !== filters.tipo) {
        return false;
      }
      
      // Filtro por rango de fechas
      if (filters.fechaDesde && new Date(m.FechaDeMantenimiento) < new Date(filters.fechaDesde)) {
        return false;
      }
      
      if (filters.fechaHasta && new Date(m.FechaDeMantenimiento) > new Date(filters.fechaHasta)) {
        return false;
      }
      
      // Filtro por rango de kilometraje
      if (filters.kilometrajeMin && m.KilometrajeActual < parseInt(filters.kilometrajeMin)) {
        return false;
      }
      
      if (filters.kilometrajeMax && m.KilometrajeActual > parseInt(filters.kilometrajeMax)) {
        return false;
      }
      
      // Filtro por serie
      if (filters.serie && (!m.Serie || !m.Serie.toLowerCase().includes(filters.serie.toLowerCase()))) {
        return false;
      }
      
      // Filtro por número económico
      if (filters.numeroEconomico && (!m.NumeroEconomico || !m.NumeroEconomico.toLowerCase().includes(filters.numeroEconomico.toLowerCase()))) {
        return false;
      }
      
      return true;
    });
    
    // Paso 3: Ordenar los resultados
    result.sort((a, b) => {
      let fieldA = a[sortField];
      let fieldB = b[sortField];
      
      // Manejar valores nulos o indefinidos
      if (fieldA === null || fieldA === undefined) fieldA = '';
      if (fieldB === null || fieldB === undefined) fieldB = '';
      
      // Convertir fechas a objetos Date para comparar
      if (sortField === 'FechaDeMantenimiento') {
        fieldA = new Date(fieldA);
        fieldB = new Date(fieldB);
      }
      
      // Convertir números a enteros para comparar
      if (sortField === 'KilometrajeActual' || sortField === 'IDMantenimiento') {
        fieldA = parseInt(fieldA) || 0;
        fieldB = parseInt(fieldB) || 0;
      }
      
      // Comparar según tipo de datos
      if (fieldA < fieldB) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (fieldA > fieldB) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
    
    return result;
  }, [maintenances, searchTerm, filters, sortField, sortDirection]);
  
  // Calcular paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAndSortedMaintenances.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAndSortedMaintenances.length / itemsPerPage);

  // Manejar descarga de Excel
  const handleDownloadExcel = async (id) => {
    try {
      console.log(`📋 Generando Excel completo para mantenimiento ${id}`);
      await maintenanceService.downloadExcelComplete(id);
    } catch (err) {
      console.error('Error al generar Excel:', err);
      setError(err.message);
    }
  };

  // Vista de cargando
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            className="w-16 h-16 border-4 border-orange-600 border-t-transparent rounded-full mx-auto mb-4"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          <p className="text-xl text-gray-600">Cargando mantenimientos...</p>
        </div>
      </div>
    );
  }

  // Funciones para la paginación
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  
  // Función para obtener el ícono de ordenamiento
  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4" />;
    return sortDirection === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />;
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Encabezado */}
        <div className="flex flex-wrap items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-3">
            <Link to="/">
              <ArrowLeft className="w-6 h-6 text-orange-600 hover:text-orange-700" />
            </Link>
            <h1 className="text-3xl font-bold text-gray-800">Mantenimientos</h1>
            <div className="ml-4">
              <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
                {filteredAndSortedMaintenances.length} registros
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Botón de consulta masiva */}
            <button
              onClick={() => setShowQueryModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-md"
            >
              <Database className="w-5 h-5" />
              <span className="hidden sm:inline">Consulta Masiva</span>
            </button>
            
            {/* Barra de búsqueda */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Buscar mantenimiento..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
            
            {/* Botón de filtros */}
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${showFilters ? 'bg-orange-100 border-orange-300 text-orange-600' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
            >
              <Filter className="w-4 h-4" />
              Filtros
              {Object.values(filters).some(v => v !== '') && (
                <span className="bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {Object.values(filters).filter(v => v !== '').length}
                </span>
              )}
            </button>
            
            {/* Selector de resultados por página */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Mostrar:</span>
              <select
                value={itemsPerPage}
                onChange={e => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>

        {/* Panel de filtros avanzados */}
        {showFilters && (
          <motion.div 
            className="bg-white p-4 rounded-lg shadow-md mb-6"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Mantenimiento</label>
                <select
                  name="tipo"
                  value={filters.tipo}
                  onChange={handleFilterChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Todos</option>
                  <option value="Preventivo">Preventivo</option>
                  <option value="Correctivo">Correctivo</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha desde</label>
                <input
                  type="date"
                  name="fechaDesde"
                  value={filters.fechaDesde}
                  onChange={handleFilterChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha hasta</label>
                <input
                  type="date"
                  name="fechaHasta"
                  value={filters.fechaHasta}
                  onChange={handleFilterChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kilometraje mínimo</label>
                <input
                  type="number"
                  name="kilometrajeMin"
                  value={filters.kilometrajeMin}
                  onChange={handleFilterChange}
                  placeholder="Min"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kilometraje máximo</label>
                <input
                  type="number"
                  name="kilometrajeMax"
                  value={filters.kilometrajeMax}
                  onChange={handleFilterChange}
                  placeholder="Max"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Serie</label>
                <input
                  type="text"
                  name="serie"
                  value={filters.serie}
                  onChange={handleFilterChange}
                  placeholder="Serie del vehículo"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">No. Económico</label>
                <input
                  type="text"
                  name="numeroEconomico"
                  value={filters.numeroEconomico}
                  onChange={handleFilterChange}
                  placeholder="Número económico"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 focus:outline-none"
                >
                  Limpiar Filtros
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Mensaje de error */}
        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 p-4 mb-6 rounded shadow-md">
            <div className="flex">
              <div>
                <p className="font-bold text-red-700">Error</p>
                <p className="text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tabla de mantenimientos */}
        {filteredAndSortedMaintenances.length > 0 ? (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      onClick={() => handleSort('IDMantenimiento')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        ID {getSortIcon('IDMantenimiento')}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('NumeroEconomico')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        No. ECONOMICO {getSortIcon('NumeroEconomico')}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('Serie')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Serie {getSortIcon('Serie')}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('TipoDeMantenimiento')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Tipo {getSortIcon('TipoDeMantenimiento')}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('FechaDeMantenimiento')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Fecha {getSortIcon('FechaDeMantenimiento')}
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('KilometrajeActual')}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        Kilometraje {getSortIcon('KilometrajeActual')}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentItems.map((maintenance) => (
                    <tr 
                      key={maintenance.IDMantenimiento} 
                      onClick={() => navigate(`/maintenances/${maintenance.IDMantenimiento}`)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {maintenance.IDMantenimiento}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {maintenance.NumeroEconomico || maintenance.numero_economico || 'No disponible'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {maintenance.Serie || 'No disponible'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          maintenance.TipoDeMantenimiento === 'Preventivo' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {maintenance.TipoDeMantenimiento || 'No especificado'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {(() => {
                          const date = new Date(maintenance.FechaDeMantenimiento);
                          const userTimezoneOffset = date.getTimezoneOffset() * 60000;
                          const localDate = new Date(date.getTime() + userTimezoneOffset);
                          return localDate.toLocaleDateString();
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {maintenance.KilometrajeActual?.toLocaleString() || '0'} km
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-3">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const serie = maintenance.Serie;
                              if (serie) {
                                navigate(`/trucks/${serie}`);
                              } else {
                                console.error('No se encontró serie para la unidad');
                              }
                            }}
                            className="text-blue-600 hover:text-blue-900"
                            title="Ver vehículo"
                          >
                            <ExternalLink className="w-5 h-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadExcel(maintenance.IDMantenimiento);
                            }}
                            className="text-orange-600 hover:text-orange-900"
                            title="Descargar Excel"
                          >
                            <Download className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Paginación */}
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex flex-col sm:flex-row justify-between items-center">
                <div className="mb-4 sm:mb-0 text-sm text-gray-700">
                  Mostrando <span className="font-medium">{indexOfFirstItem + 1}</span> a <span className="font-medium">
                    {Math.min(indexOfLastItem, filteredAndSortedMaintenances.length)}
                  </span> de <span className="font-medium">{filteredAndSortedMaintenances.length}</span> resultados
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={prevPage}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-md border ${
                      currentPage === 1
                        ? 'border-gray-300 bg-white text-gray-300 cursor-not-allowed'
                        : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    {Array.from({ length: Math.min(5, totalPages) }).map((_, index) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = index + 1;
                      } else if (currentPage <= 3) {
                        pageNum = index + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + index;
                      } else {
                        pageNum = currentPage - 2 + index;
                      }
                      
                      return (
                        <button
                          key={index}
                          onClick={() => paginate(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === pageNum
                              ? 'z-10 bg-orange-50 border-orange-500 text-orange-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </nav>
                  
                  <button
                    onClick={nextPage}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-md border ${
                      currentPage === totalPages
                        ? 'border-gray-300 bg-white text-gray-300 cursor-not-allowed'
                        : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <FileCheck className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No se encontraron mantenimientos</h3>
            <p className="text-gray-500">
              {searchTerm || Object.values(filters).some(v => v !== '') 
                ? 'Intenta con otros filtros o términos de búsqueda' 
                : 'Aún no hay mantenimientos registrados en el sistema'}
            </p>
            {(searchTerm || Object.values(filters).some(v => v !== '')) && (
              <button
                onClick={clearFilters}
                className="mt-4 px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modal de consulta masiva */}
      <MaintenanceQueryModal 
        isOpen={showQueryModal}
        onClose={() => setShowQueryModal(false)}
        trucks={trucks}
      />
    </div>
  );
};

export default MaintenancesPage;